<script setup lang="ts">
import type { LedgerItem } from '@satisa-memory/shared'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { api, unwrap } from '../lib/api'
import { KIND_LABELS } from '../lib/ledger'
import { useLedger } from '../lib/ledger-state'
import { useLibrary } from '../lib/library'
import { newPersonLabel } from '../lib/people'
import { usePersonPicker } from '../lib/person-picker'
import {
  emptyDraft,
  imageIn,
  isTyping,
  screenshotProblem,
  TODO_KINDS,
  type TodoDraft,
  todoForm
} from '../lib/todo'

/**
 * The quick add, on every page: a + in the corner (or `n`, or pasting a
 * screenshot anywhere) opens a small form for something the owner owes or
 * keeps track of, or a decision to record. Only the line is required. A
 * decision has a date (when it takes effect, blank for today) where a to-do
 * has a due date; the Worker keeps both in one field. It is stored as a ledger item
 * with a note for its source (worker db/todos.ts), which the sync and the
 * ledger task then enrich like any saved link. Both lists, the ledger's and
 * the library's, are read again once it is in.
 */

const { added } = useLedger()
const library = useLibrary()
const dialog = ref<HTMLDialogElement | null>(null)
const textEl = ref<HTMLInputElement | null>(null)
const fileEl = ref<HTMLInputElement | null>(null)
const draft = ref<TodoDraft>(emptyDraft())
const saving = ref(false)
const error = ref<string | null>(null)
const dragging = ref(false)
const preview = ref<string | null>(null)
/**
 * The last one added, for the confirmation with its link, and the items a
 * meeting had already recorded of it, now folded into it (worker item-merge.ts).
 */
const done = ref<(LedgerItem & { merged: { link: { title: string } }[] }) | null>(null)
let doneTimer: ReturnType<typeof setTimeout> | undefined

const decision = computed(() => draft.value.kind === 'decision')

function open(shot: File | null = null) {
  if (dialog.value?.open) {
    if (shot) setScreenshot(shot)
    return
  }
  draft.value = emptyDraft()
  error.value = null
  picker.clear()
  if (shot) setScreenshot(shot)
  dialog.value?.showModal()
  void nextTick(() => textEl.value?.focus())
}

function close() {
  dialog.value?.close()
}

function setScreenshot(f: File | null) {
  if (preview.value) URL.revokeObjectURL(preview.value)
  preview.value = null
  if (f) {
    const problem = screenshotProblem(f)
    if (problem) {
      error.value = problem
      return
    }
    error.value = null
    preview.value = URL.createObjectURL(f)
  }
  draft.value.screenshot = f
}

function onFile(e: Event) {
  const input = e.target as HTMLInputElement
  setScreenshot(input.files?.[0] ?? null)
  input.value = ''
}

function onDrop(e: DragEvent) {
  dragging.value = false
  const f = imageIn(e.dataTransfer)
  if (f) setScreenshot(f)
}

// ---- people -----------------------------------------------------------------

const picker = usePersonPicker(() => draft.value.people.map((p) => p.id))
const {
  query: personQuery,
  hits: personHits,
  candidate,
  adding: addingPerson,
  error: personError
} = picker

function addPerson(p: { id: string; displayName: string }) {
  if (!draft.value.people.some((q) => q.id === p.id)) {
    draft.value.people.push({ id: p.id, name: p.displayName })
  }
  picker.clear()
}

/** Someone no source has met yet: added to the people, then to the to-do. */
async function addNewPerson() {
  const p = await picker.addCandidate()
  if (p) addPerson(p)
}

function removePerson(id: string) {
  draft.value.people = draft.value.people.filter((p) => p.id !== id)
}

function onPersonKey(e: KeyboardEvent) {
  if (e.key === 'Enter' && (personHits.value[0] || candidate.value)) {
    e.preventDefault()
    if (personHits.value[0]) addPerson(personHits.value[0])
    else void addNewPerson()
  } else if (e.key === 'Backspace' && !personQuery.value && draft.value.people.length) {
    draft.value.people.pop()
  }
}

// ---- saving -----------------------------------------------------------------

async function save() {
  if (!draft.value.text.trim() || saving.value) return
  saving.value = true
  error.value = null
  try {
    const item = await unwrap(api.api.todos.$post({ form: todoForm(draft.value) }))
    added()
    void library.refresh()
    setScreenshot(null)
    close()
    done.value = item
    clearTimeout(doneTimer)
    doneTimer = setTimeout(() => (done.value = null), 6000)
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not save'
  } finally {
    saving.value = false
  }
}

// ---- opening it from anywhere ------------------------------------------------

function onKey(e: KeyboardEvent) {
  if (e.metaKey || e.ctrlKey || e.altKey || dialog.value?.open || isTyping(e.target)) return
  if (e.key === 'n') {
    e.preventDefault()
    open()
  }
}

/** A screenshot pasted on any page, outside a field, starts a to-do from it. */
function onPaste(e: ClipboardEvent) {
  if (isTyping(e.target) && !dialog.value?.open) return
  const f = imageIn(e.clipboardData)
  if (!f) return
  e.preventDefault()
  open(f)
}

/** The ⌘K palette's "Add a to-do or decision". */
const openFromPalette = () => open()

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener('paste', onPaste)
  window.addEventListener('memory:quick-add', openFromPalette)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('paste', onPaste)
  window.removeEventListener('memory:quick-add', openFromPalette)
  if (preview.value) URL.revokeObjectURL(preview.value)
  clearTimeout(doneTimer)
})
</script>

<template>
  <button
    type="button"
    class="fab"
    aria-label="Add a to-do or decision"
    title="Add a to-do or decision (n) — or paste a screenshot"
    @click="open()"
  >
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <path
        d="M10 4v12M4 10h12"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  </button>

  <p v-if="done" class="toast" role="status">
    {{ done.kind === 'decision' ? 'Decision recorded.' : 'To-do added.' }}
    <template v-if="done.merged.length">
      Merged with the one from {{ done.merged.map((m) => m.link.title).join(', ') }}.
    </template>
    <RouterLink :to="{ name: 'ledger-item', params: { itemId: done.id } }" @click="done = null"
      >Open</RouterLink
    >
  </p>

  <dialog
    ref="dialog"
    class="sheet"
    :aria-label="decision ? 'Record a decision' : 'Add a to-do'"
    @click.self="close"
    @close="setScreenshot(null)"
  >
    <form
      class="body"
      :class="{ dragging }"
      @submit.prevent="save"
      @dragover.prevent="dragging = true"
      @dragleave.self="dragging = false"
      @drop.prevent="onDrop"
    >
      <input
        ref="textEl"
        v-model="draft.text"
        class="input text"
        maxlength="300"
        :placeholder="decision ? 'What was decided?' : 'What do you owe, or want to keep track of?'"
        :aria-label="decision ? 'Decision' : 'To-do'"
        required
      />

      <div class="kinds" role="radiogroup" aria-label="Kind">
        <button
          v-for="k in TODO_KINDS"
          :key="k"
          type="button"
          role="radio"
          :aria-checked="draft.kind === k"
          class="kind"
          :class="{ on: draft.kind === k }"
          @click="draft.kind = k"
        >
          {{ KIND_LABELS[k] }}
        </button>
      </div>

      <div class="field people">
        <span
          v-for="(p, i) in draft.people"
          :key="p.id"
          class="chip person"
          :title="
            i === 0
              ? decision
                ? 'Who it most concerns'
                : 'Who it is owed to, or by'
              : 'Also involved'
          "
        >
          {{ p.name }}<span v-if="i === 0 && draft.people.length > 1" class="muted"> ·main</span>
          <button type="button" :aria-label="`Remove ${p.name}`" @click="removePerson(p.id)">
            ×
          </button>
        </span>
        <input
          v-model="personQuery"
          class="bare"
          :placeholder="
            draft.people.length
              ? 'Someone else?'
              : decision
                ? 'Who does it concern?'
                : 'Who is it with?'
          "
          aria-label="People"
          @keydown="onPersonKey"
        />
      </div>
      <ul v-if="personHits.length || candidate" class="hits">
        <li v-for="p in personHits" :key="p.id">
          <button type="button" class="btn ghost" @click="addPerson(p)">
            {{ p.displayName }} <span v-if="p.email" class="muted">{{ p.email }}</span>
          </button>
        </li>
        <li v-if="candidate">
          <button
            type="button"
            class="btn ghost new"
            :disabled="addingPerson"
            title="Not in any sync yet: add them to your people"
            @click="addNewPerson"
          >
            + Add {{ newPersonLabel(candidate) }}
            <span class="muted">as someone new</span>
          </button>
        </li>
      </ul>
      <p v-if="personError" class="error">{{ personError }}</p>

      <textarea
        v-model="draft.notes"
        class="input notes"
        rows="2"
        maxlength="4000"
        placeholder="Notes (optional)"
        aria-label="Notes"
      />

      <div v-if="preview" class="shot">
        <img :src="preview" alt="Screenshot" />
        <button type="button" class="btn ghost small" @click="setScreenshot(null)">
          Remove screenshot
        </button>
      </div>

      <div class="line">
        <label
          class="due muted"
          :title="decision ? 'When it takes effect; blank is today' : undefined"
        >
          {{ decision ? 'Date' : 'Due' }}
          <input v-model="draft.dueDate" type="date" class="input" />
        </label>
        <button
          v-if="!preview"
          type="button"
          class="btn ghost attach"
          title="Or paste or drop one here"
          @click="fileEl?.click()"
        >
          Screenshot…
        </button>
        <input
          ref="fileEl"
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          @change="onFile"
        />
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="line actions">
        <span class="hint muted">Paste or drop a screenshot · ↵ to add</span>
        <button type="button" class="btn" @click="close">Cancel</button>
        <button type="submit" class="btn primary" :disabled="saving || !draft.text.trim()">
          {{ saving ? 'Adding…' : 'Add' }}
        </button>
      </div>
    </form>
  </dialog>
</template>

<style scoped>
.fab {
  position: fixed;
  z-index: 40;
  right: calc(1.25rem + env(safe-area-inset-right));
  bottom: calc(1.25rem + env(safe-area-inset-bottom));
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  color: var(--on-accent);
  background: var(--accent);
  border: 0;
  border-radius: 50%;
  box-shadow: var(--shadow-md);
  cursor: pointer;
  opacity: 0.85;
  transition:
    opacity 0.15s,
    transform 0.15s;
}

.fab:hover,
.fab:focus-visible {
  opacity: 1;
  transform: scale(1.06);
  background: var(--accent-hover);
}

.toast {
  position: fixed;
  z-index: 40;
  right: calc(1.25rem + env(safe-area-inset-right));
  bottom: calc(4.75rem + env(safe-area-inset-bottom));
  margin: 0;
  padding: 0.5rem 0.85rem;
  font-size: 13px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
}

.sheet {
  width: min(520px, calc(100vw - 2rem));
  max-height: calc(100dvh - 2rem);
  padding: 0;
  color: var(--fg);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
}

.sheet::backdrop {
  background: rgba(10, 8, 4, 0.35);
}

.body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem;
}

.body.dragging {
  outline: 2px dashed var(--accent-line);
  outline-offset: -6px;
}

.text {
  font-size: 15px;
}

.kinds {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.kind {
  padding: 0.25rem 0.65rem;
  font: inherit;
  font-size: 12.5px;
  color: var(--fg-2);
  background: var(--surface-2);
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
}

.kind.on {
  color: var(--accent);
  background: var(--accent-soft);
  border-color: var(--accent-line);
  font-weight: 600;
}

.field.people {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.5rem;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
}

.field.people:focus-within {
  border-color: var(--accent-line);
}

.person button {
  margin-left: 0.2rem;
  padding: 0;
  font: inherit;
  color: var(--muted);
  background: none;
  border: 0;
  cursor: pointer;
}

.bare {
  flex: 1;
  min-width: 8rem;
  padding: 0.15rem 0;
  font: inherit;
  font-size: 13.5px;
  color: var(--fg);
  background: none;
  border: 0;
  outline: none;
}

.hits {
  margin: -0.3rem 0 0;
  padding: 0;
  list-style: none;
}

.hits .btn {
  justify-content: flex-start;
  width: 100%;
}

.hits .new {
  color: var(--accent);
}

.notes {
  resize: vertical;
  font-size: 13.5px;
}

.shot {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.shot img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
}

.line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.due {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 12.5px;
}

.due .input {
  width: auto;
}

.attach {
  font-size: 12.5px;
}

.actions {
  justify-content: flex-end;
  margin-top: 0.15rem;
}

.hint {
  margin-right: auto;
  font-size: 12px;
}

.error {
  margin: 0;
  font-size: 12.5px;
  color: var(--danger);
}

.small {
  font-size: 12px;
}

@media (max-width: 600px) {
  .fab {
    right: calc(1rem + env(safe-area-inset-right));
    bottom: calc(1rem + env(safe-area-inset-bottom));
  }

  /* A sheet from the bottom, above the keyboard. */
  .sheet {
    width: 100vw;
    max-width: 100vw;
    margin: auto 0 0;
    border-radius: var(--radius) var(--radius) 0 0;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .hint {
    display: none;
  }
}
</style>
